import {AbstractDialogComponent} from "../../ha-dialog/abstract-dialog.component";
import {DialogCommandName} from "../../ha-dialog/dialog-command.enum";
import {customElement} from "lit/decorators/custom-element.js";
import {html, HTMLTemplateResult, PropertyValues} from "lit";
import {property} from "lit/decorators/property.js";
import {Project} from "../../../domain/entities/project.entity";
import {get, translate} from "lit-translate";
import {
    bindTo,
    ComplexFormControl,
    FieldType,
    FormBuilder,
    FormControlNotification,
    PatternStringValidator,
    RequiredValidator,
    UrlStringValidator
} from "@ha/formhandler";
import {DialogHelper} from "../../../helpers/dialog.helper";
import {CreateProjectCommand} from "../../../commands/create-project.command";
import {UpdateProjectCommand} from "../../../commands/update-project.command";
import {container} from "tsyringe";
import {CommandBus} from "../../../services/command-bus.service";
import {MessageBroker} from "../../../message-broker/message-broker";
import {BaseMessage} from "../../../message-broker/base-message.interface";
import {EntityChangedPayload} from "../../../message-broker/payloads/entity-changed.payload";
import {EntityCreatedPayload} from "../../../message-broker/payloads/entity-created.payload";
import {Employee} from "../../../domain/entities/employee.entity";
import {filter, merge, Subject, takeUntil} from "rxjs";
import {coerceUrlToNullIfBlank} from "../../../helpers/url-coercion.helper";

type FormData = {
    title: string;
    creator: string;
    assignees: string[];
    url: string;
    description: string;
    color: string;
}

@customElement(`ha-dialog-daily-edit`)
export class HaDialogDailyEdit extends AbstractDialogComponent {

    @property({type: Object})
    public daily: Project | null = null;
    @property({type: String})
    public title = `Daily Edit`;
    private _initialTitle: string | null = null;
    private _form: ComplexFormControl<FormData, void> | null = null;
    private _changedValues: Map<string, any> = new Map<string, any>();
    /** Ends the streams of the current form; the form is rebuilt on every reset. */
    private readonly _formTeardown$ = new Subject<void>();

    private get _isSavable(): boolean {
        return this.daily!.isNew()
            ? this._form?.isValid ?? false
            : (this._form?.isValid && this._form.isDirty) ?? false;
    };

    public connectedCallback(): void {
        super.connectedCallback();

        // Reset the form once the daily shown here was saved. Registered on the element:
        // @OnMessage only works on AbstractBaseListener subclasses, and its filter ran
        // without an instance. The filter reads `daily` per message, because DialogHelper
        // may hand this element another daily.
        for (const messageType of [`entity.created`, `entity.changed`]) {
            container.resolve(MessageBroker).registerListener(this, {
                messageType,
                filterFn: (message: BaseMessage<EntityCreatedPayload<Project> | EntityChangedPayload<Project>>) =>
                    message.payload.uid === this.daily?.uid,
                method: this.onDailySuccessfullyPersisted,
                disconnect: this._disconnected$.asObservable(),
            });
        }
    }

    /** @internal Public so unit tests can run the lifecycle step Lit calls. */
    public willUpdate(changedProperties: PropertyValues) {
        if (changedProperties.has(`daily`)) {
            this._initializeForm();
        }
    }

    protected _renderMainContent(): unknown {
        if (!this._form) {
            return html`
                <div>Loading...</div>`;
        }

        return html`
            <ha-form-daily>
                <header class="formular__header">
                    ${this._renderActionButtons()}
                </header>
                <main class="formular__main">

                    <ha-input-text class="max9999-flexrow" data-type="title"
                                   label=${translate('components.ha-dialog.title')}
                                   ${bindTo(this._form, `title`)}
                    ></ha-input-text>

                    

                    <ha-combobox class="max9999-flexrow"
                                 data-type="creator"
                                 label=${translate('general.creator')}
                                 ${bindTo(this._form, `creator`)}
                                 .entity=${Employee}
                    ></ha-combobox>

                    <ha-input-text class="max9999-flexrow"
                                   data-type="url"
                                   label=${translate('components.ha-dialog.url')}
                                   placeholder="http://domain.tld"
                                   ${bindTo(this._form, `url`)}
                    ></ha-input-text>

                    <ha-input-text class="max9999-flexrow" data-type="description"
                                   label=${translate('components.ha-dialog.description')}
                                   ${bindTo(this._form, `description`)}
                    ></ha-input-text>
                    <ha-input-color class="max9999-flexrow" data-type="color"
                                    label=${translate('components.ha-dialog.color')}
                                    .entity=${Project}
                                    ${bindTo(this._form, `color`)}
                    ></ha-input-color>
                </main>
            </ha-form-daily>
        `;
    }

    protected onClickClose(): void {
        DialogHelper.closeDialog(this.tagName);
        super.onClickClose();
    }

    protected onDailySuccessfullyPersisted(): void {
        this._resetForm();
    }

    private _initializeForm() {
        this._formTeardown$.next();
        this._form = FormBuilder.buildForm(`daily`, [
            {
                type: FieldType.Primitive,
                key: `title`,
                initialValue: this.daily!.title ?? ``,
                validations: []
            },
            {
                type: FieldType.Primitive,
                key: `creator`,
                initialValue: this.daily!.creator ?? null,
                validations: [
                    new RequiredValidator()
                ]
            },
            {
                type: FieldType.Primitive,
                key: `url`,
                // ?? not ||: `||` also replaces the EMPTY string, which is what put the
                // sample text into the field as a real value and then saved it.
                initialValue: this.daily!.url ?? null,
                validations: [
                    new UrlStringValidator()
                ]
            },
            {
                type: FieldType.Primitive,
                key: `description`,
                initialValue: this.daily!.description ?? ``,
                validations: []
            },
            {
                type: FieldType.Primitive,
                key: `color`,
                initialValue: this.daily!.color ?? `#FFFFFF`,
                validations: [
                    new PatternStringValidator({
                        pattern: new RegExp(`^#([0-9a-fA-F]{3}){1,2}$`),
                        validationMessageFn: () => get(`Bitte geben Sie ein #RGB-Farbwert ein (z.B. #F0D0B0)`)
                    })
                ]
            },
        ]);

        const formEnd$ = merge(this._formTeardown$, this._disconnected$);
        const formEvent$ = this._form.event$.pipe(
            takeUntil(formEnd$)
        );

        formEvent$.pipe(
            filter(value => value.event === `value.changed` && value.formControl!.key === `title`)
        ).subscribe(notification => {
            if (!this._initialTitle) {
                this._initialTitle = this.title;
            }
            // The filter above keeps only the `title` field, a primitive built with a string initial value.
            this.title = get(`ha-boards-view.add-entity.with-placeholder.add-daily`, {title: notification.formControl!.getValue() as string})
        });

        // render after field added or removed
        this._form.event$.pipe(
            takeUntil(formEnd$),
            filter((notification: FormControlNotification) => notification.event.startsWith(`childFormControl.`) || notification.event === "value.changed")
        ).subscribe(notification => {
            const [, ...subPath] = notification.formControl!.path.split(`.`);
            const path = subPath.join(`.`);
            this._changedValues.set(path as keyof FormData, notification);
            this.requestUpdate(`_form`, null)
        });
    }

    private _renderActionButtons(): HTMLTemplateResult {
        return html`
            <div class="button-group">
                <button type="button"
                        class="save buttonform-marker primary"
                        ?disabled=${!this._isSavable}
                        @click=${this._onClickSaveButton}
                
                        data-label=${translate('general.save')}><div class="hover-effect"></div>
                </button>
                <button type="button"
                        class="save-and-close buttonform-marker primary"
                        ?disabled=${!this._isSavable}
                        @click=${this._onClickSaveAndCloseButton}
                
                        data-label=${translate('general.save-and-close')}><div class="hover-effect"></div>
                </button>
                <button type="button"
                        class="reset buttonform-marker secondary"
                        data-label=${translate('general.reset')}
                        ?disabled=${!this._form?.isDirty}
                        @click=${() => this._resetForm()}
                >
                    <div class="hover-effect"></div>
                </button>
            </div>
        `;
    }

    /** Saves the entity and dispatches a CLOSE command to dismiss the dialog. */
    private _onClickSaveAndCloseButton(): void {
        this._onClickSaveButton();
        this.dispatchCommand({
            command: DialogCommandName.CLOSE,
            payload: void 0
        });
    }

    private _onClickSaveButton(): void {

        for (const [path, notification] of this._changedValues.entries()) {
            switch (path) {
                // A daily IS a project, so its URL needs the same coercion. Without this
                // case the generic branch below writes an emptied field back as '' and the
                // two spellings of "no URL" reappear on this path alone.
                case `url`:
                    this.daily!.url = coerceUrlToNullIfBlank(
                        notification.formControl!.getValue() as string | null,
                    );
                    break;
                default:
                    Reflect.set(this.daily!, path, notification.formControl!.getValue());
            }
        }

        const command = this.daily!.isNew()
            ? new CreateProjectCommand(this.daily!)
            : new UpdateProjectCommand(this.daily!);

        container.resolve(CommandBus).dispatchCommand(command).then();
    }

    private _resetForm(): void {
        this._changedValues.clear();
        this._initializeForm();
        this.title = this._initialTitle ?? `Daily Edit`;
        this.requestUpdate();
    }
}